import { createEffect, createSignal, Show } from 'solid-js'
import { native } from '@forest/native-services'
import { showDialog } from '~/stores/dialog'
import { events, getEventById, type CampaignEvent } from '~/stores/events'
import { Devtools } from './components/devtools'
import { Dialog } from './components/dialog'
import { LoadingOverlay } from './components/loading-overlay'
import { m } from './paraglide/messages'
import { params } from './params'

export function App() {
  let navigateGate = false
  const [isLoading, setIsLoading] = createSignal(false)
  const [selectedEvent, setSelectedEvent] = createSignal<null | string>(null)

  createEffect(() => {
    const evt = selectedEvent()
    if (evt) {
      const event = getEventById(evt)
      if (!event) return
      const url = new URL(event.url)
      params.delete('ts')
      url.search = params.toString()
      location.assign(url)
    }
  })

  const handleEventClick = (event: CampaignEvent) => {
    if (event.isUnderMaintenance) {
      showDialog({
        title: m.dialog_event_pause_title(),
        description: m.dialog_event_pause_description(),
        primaryButtonText: m.ok(),
      })
      return
    }
    if (navigateGate) return
    navigateGate = true
    setIsLoading(true)
    setSelectedEvent(event.id)
  }

  const handleBackToHome = () => {
    native.general.close()
  }

  return (
    <div
      class="relative flex h-full w-full flex-col items-center justify-between overflow-y-auto pb-12 pt-[calc(48px+var(--safe-area-top))]"
      style={{ background: BG_GRADIENT }}
    >
      <div class="flex w-full flex-1 flex-col items-center">
        <div class="flex w-full max-w-[640px] flex-col items-center gap-6 px-9">
          {/* Page title */}
          <h1 class="m-0 w-full text-center text-xl font-[590] leading-7 text-[#036059]">
            {m.Event_list_title()}
          </h1>

          {/* Event cards */}
          <div class="flex w-full flex-col gap-6">
            {events.flatMap((event) => {
              const badge = event.isUnderMaintenance
                ? {
                    text: m.event_state_pause(),
                    bgClass: 'bg-[rgba(160,21,47,0.50)]',
                  }
                : (() => {
                    const text = getEventBadge(event)
                    return text === null ? null : { text }
                  })()
              if (badge === null) return []
              return [
                <EventCard
                  event={event}
                  badge={badge}
                  onClick={handleEventClick}
                />,
              ]
            })}
          </div>
        </div>
      </div>

      {/* Bottom button */}
      <div class="flex w-full justify-center">
        <BackToHomeButton onClick={handleBackToHome} />
      </div>

      <LoadingOverlay isLoading={isLoading()} />

      <Dialog />

      <Show when={import.meta.env.MODE !== 'production'}>
        <Devtools />
      </Show>
    </div>
  )
}

// ─── Badge text from event dates ───
function getEventBadge(event: CampaignEvent): string | null {
  const now = Date.now()
  const start = new Date(event.validStartDate).getTime()
  const end = new Date(event.validEndDate).getTime()
  const visible = new Date(event.visibleEndDate).getTime()

  if (now >= visible) return null
  if (now < start) return m.event_state_coming_soon()

  if (now >= end) {
    const claimMs = visible - now
    const h = Math.floor(claimMs / (1000 * 60 * 60))
    const min = Math.floor((claimMs / (1000 * 60)) % 60)
    return m.event_state_claim_period({ hour: h, minute: min })
  }

  const msLeft = end - now
  const hoursLeft = msLeft / (1000 * 60 * 60)

  if (hoursLeft <= 24) {
    const h = Math.floor(hoursLeft)
    const min = Math.floor((msLeft / (1000 * 60)) % 60)
    return m.event_state_time_left({ hour: h, minute: min })
  }

  const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24))
  const hoursRemainder = Math.floor(hoursLeft % 24)
  return m.event_state_days_left({ day: daysLeft, hour: hoursRemainder })
}

// ─── Badge component ───
function Badge(props: { text: string; bgClass?: string }) {
  return (
    <div
      class={`flex h-5 items-center justify-center rounded-[9px] px-2 py-0.5 ${
        props.bgClass ?? 'bg-[rgba(147,144,51,0.26)]'
      }`}
    >
      <span class="whitespace-nowrap text-xs leading-4 text-white">
        {props.text}
      </span>
    </div>
  )
}

// ─── Event Card ───
function EventCard(props: {
  event: CampaignEvent
  badge: { text: string; bgClass?: string }
  onClick: (event: CampaignEvent) => void
}) {
  const [pressed, setPressed] = createSignal(false)

  return (
    <button
      onClick={() => {
        props.onClick(props.event)
      }}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      class="relative flex w-full cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-[30px] px-6 pb-3 pt-4 ring-1 ring-inset ring-[rgba(247,247,247,0.4)]"
      style={{
        '-webkit-tap-highlight-color': 'transparent',
        'background-image': `url(${props.event.bgImg})`,
        'background-position': props.event.bgPosition ?? '50% 50%',
        'background-size': 'cover',
        'background-repeat': 'no-repeat',
      }}
    >
      {/* Content row */}
      <div class="relative flex min-w-0 flex-1 items-center gap-4">
        {/* Tree icon */}
        <div class="relative size-20 shrink-0">
          <img
            src={props.event.iconImg}
            alt=""
            class="pointer-events-none absolute inset-0 size-full object-contain"
          />
        </div>

        {/* Event title */}
        <div class="min-w-0 flex-1">
          <p class="m-0 text-left text-base font-semibold leading-[22px] text-white">
            {props.event.title()}
          </p>
        </div>
      </div>

      {/* Badge */}
      <div class="absolute right-[12.5px] top-[9.5px] flex flex-col items-end">
        <Badge text={props.badge.text} bgClass={props.badge.bgClass} />
      </div>

      {/* Press overlay */}
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-black/40 transition-opacity"
        classList={{ 'opacity-0': !pressed(), 'opacity-100': pressed() }}
      />
    </button>
  )
}

// ─── Back to Home Button ───
function BackToHomeButton(props: { onClick: () => void }) {
  return (
    <button
      onClick={() => props.onClick()}
      class="flex h-[120px] w-full max-w-[640px] shrink-0 cursor-pointer items-center justify-center rounded-xl p-4 shadow-[0_0_20px_-2px_rgba(198,255,205,0.15)]"
    >
      <span class="text-center text-base font-bold text-[#176361]">
        {m.back_to_home()}
      </span>
    </button>
  )
}

const BG_GRADIENT = `radial-gradient(ellipse at 66% 12%, rgba(255,255,255,1) 0%, rgba(235,254,243,1) 40%, rgba(197,239,217,1) 65%, rgba(159,224,192,1) 100%)`
